import React, { useState, useEffect, useRef } from "react";
import { Box, DialogActions, DialogContent, Button, LinearProgress, Typography, IconButton, CircularProgress } from "@mui/material";
import { TextInputComponent } from "../../atom";
import TextButtonComponent from "../../atom/Buttons/TextButton";
import { Actions } from "../../../core/modules/Actions";
import { useDispatch, useSelector } from 'react-redux';
import PopUpMessageComponent from "../PopupMessage/PopUpMessage";
import { getText, getFile, onToast, THEME_COLORS, getThumbnailSrc } from "../../../core/Constant";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCloudArrowUp, faImage, faVideo, faXmark } from "@fortawesome/free-solid-svg-icons";
import axios from 'axios'; // Import Axios
import { baseURL } from "../../../core/repository/Repository";

const VideoForm = ({ onClose, isClsose, videoData }) => {
  const dispatch = useDispatch();
  const inputRef = useRef();
  const imageRef = useRef();

  const isEditMode = Boolean(videoData && (videoData.id || videoData.video_id));

  const [title, setTitle] = useState('');
  const [titleError, setTitleError] = useState(false);
  const [titleValid, setTitleValid] = useState(false);

  const [video, setVideo] = useState('');
  const [videoError, setVideoError] = useState(false);
  const [videoErrorMsg, setVideoErrorMsg] = useState('');
  const [isVideoValid, setIsVideoValid] = useState(false);

  const [thumbnail, setThumbnail] = useState('');
  const [thumbnailError, setThumbnailError] = useState(false);
  const [thumbnailErrorMsg, setThumbnailErrorMsg] = useState('');
  const [isThumbnailValid, setThumbnailValid] = useState(false);
  const [currentThumbnail, setCurrentThumbnail] = useState('');
  const [previewUrl, setPreviewUrl] = useState("");

  const handleRemoveThumbnail = () => {
    setThumbnail('');
    setCurrentThumbnail('');
    setThumbnailValid(false);
    if (imageRef.current) {
      imageRef.current.value = "";
    }
  };

  useEffect(() => {
    if (!thumbnail) {
      setPreviewUrl("");
      return;
    }
    if (thumbnail instanceof File || thumbnail instanceof Blob) {
      const url = URL.createObjectURL(thumbnail);
      setPreviewUrl(url);
      return () => {
        URL.revokeObjectURL(url);
      };
    }
  }, [thumbnail]);

  const [videoUploadStatus, setVideoUploadStatus] = useState("");

  const [videoUploadBtnStatus, setVideoUploadBtnStatus] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadSpeed, setUploadSpeed] = useState(0);
  const [elapsedTime, setElapsedTime] = useState(0);

  const token = localStorage.getItem('token') || '';

  useEffect(() => {
    if (isEditMode) {
      const initialTitle = videoData.title || videoData.description || videoData.title_name || "";
      setTitle(initialTitle);
      setTitleValid(Boolean(initialTitle?.length > 0));
      setTitleError(false);
      setVideo('');
      setThumbnail('');
      setCurrentThumbnail(videoData.thumbnail || '');
      if (inputRef.current) inputRef.current.value = "";
      if (imageRef.current) imageRef.current.value = "";
      setVideoUploadBtnStatus(false);
      setVideoError(false);
      setThumbnailError(false);
      setThumbnailValid(false);
      setIsVideoValid(false);
      setVideoUploadStatus("");
      setUploadProgress(0);
      setUploadSpeed(0);
      setElapsedTime(0);
    } else {
      setTitle('');
      setVideo('');
      setThumbnail('');
      setCurrentThumbnail('');
      setVideoUploadStatus("");
      if (inputRef.current) inputRef.current.value = "";
      if (imageRef.current) imageRef.current.value = "";
      setVideoUploadBtnStatus(false);
      setTitleError(false);
      setVideoError(false);
      setThumbnailError(false);
      setTitleValid(false);
      setThumbnailValid(false);
      setIsVideoValid(false);
      setUploadProgress(0);
      setUploadSpeed(0);
      setElapsedTime(0);
    }
  }, [isClsose, videoData]);

  const onTitleChange = (e) => {
    const text = getText(e);
    setTitle(text);
    setTitleError(false);
    setTitleValid(text?.length > 0);
  };

  const onThumbnailChange = (e) => {
    const file = getFile(e);
    setThumbnailError(false);
    const isValid = file !== "" && file !== undefined && file !== null;
    setThumbnailValid(isValid);
    setThumbnail(isValid ? file : '');
  };

  const onVideoChange = (e) => {
    const file = getFile(e);
    const isValid = file !== "" && file !== undefined && file !== null;
    setIsVideoValid(isValid);
    setVideoError(false);
    setVideo(isValid ? file : '');
  };

  const adminVideoUpload = () => {
    if (isVideoValid && titleValid) {
      setVideoUploadStatus("Uploading...");
      setVideoUploadBtnStatus(true);

      const data = new FormData();
      data.append("video", video);
      data.append("title", title);
      if (thumbnail) {
        data.append("thumbnail", thumbnail);
      }

      // Dummy field to force multipart/chunked upload
      data.append("dummy", "dummy-value");

      const config = {
        headers: {
          Authorization: token ? `Bearer ${token}` : `Bearer`,
          "Cache-Control": "no-cache, no-store, must-revalidate",
          Pragma: "no-cache",
          Accept: "application/json",
        },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.lengthComputable) {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(progress);

            const currentTime = new Date().getTime();
            const timeElapsed = (currentTime - startTime) / 1000;
            setElapsedTime(timeElapsed);

            const uploadedBytes = progressEvent.loaded;
            const speedKBps = uploadedBytes / timeElapsed / 1024;
            const speedMbps = (speedKBps * 8) / 1000;
            setUploadSpeed(speedMbps);
          }
        },
        transformRequest: [data => data],
      };

      let startTime = new Date().getTime();

      axios.post(baseURL + "api/v1/admin/video/upload", data, config)
        .then((response) => {
          setVideoUploadStatus("Upload successful!");
          dispatch(Actions.video.fetchVideo());
          onToast('Upload', { status_code: response.data.status_code, message: response.data.message });
          onClose();
        })
        .catch((error) => {
          setVideoUploadStatus("Upload failed. Please try again.");
          console.error(error);
        })
        .finally(() => {
          setVideoUploadBtnStatus(false);
          setUploadProgress(0);
          setUploadSpeed(0);
        });
    } else {
      if (!titleValid) setTitleError(true);
      if (!isVideoValid) {
        setVideoErrorMsg('Please select the video');
        setVideoError(true);
      }
    }
  };

  const runSave = () => {
    const videoId = videoData.id || videoData.video_id;
    const data = new FormData();
    data.append("id", videoId);
    data.append("title", title.trim());
    if (thumbnail) {
      data.append("thumbnail", thumbnail);
    } else if (!currentThumbnail) {
      data.append("delete_thumbnail", 1);
    }
    dispatch(Actions.video.updateVideoTitle(data));
    onClose();
  };

  const handleFormSubmit = async () => {
    if (isEditMode) {
      if (titleValid && title?.trim().length > 0) {
        runSave();
      } else {
        setTitleError(true);
      }
    } else {
      adminVideoUpload();
    }
  };

  const currentThumbnailName = thumbnail
    ? thumbnail.name
    : (currentThumbnail ? currentThumbnail.split('/').pop() : '');

  return (
    <>
      <form onSubmit={(e) => { e.preventDefault(); handleFormSubmit(); }}>
        <DialogContent>
          <Box>
            <Box>
              <div style={{ marginBottom: '5px' }}>
                <TextInputComponent
                  label={"Title"}
                  isError={titleError}
                  placeholder="Enter video title"
                  name={"title"}
                  value={title}
                  error={"Please add the video title"}
                  onchange={onTitleChange}
                />
              </div>
              {
                !isEditMode &&
                <div style={{ marginBottom: '5px' }}>
                  <Box style={{ padding: 10 }}>
                    <label className="form-label">Video</label>
                    <input
                      className="form-control"
                      type={"file"}
                      accept="video/*"
                      ref={inputRef}
                      onChange={onVideoChange}
                      required
                    />
                    {videoError ? <p className="input-error-text">{videoErrorMsg}</p> : null}
                  </Box>
                </div>
              }
              <div style={{ marginBottom: '5px' }}>
                <Box sx={{ padding: "10px", display: "flex", flexDirection: "column", gap: 1, alignItems: "flex-start" }}>
                  <label className="form-label" style={{ margin: 0 }}>Thumbnail</label>
                  {(!isEditMode || !currentThumbnail) && (
                    <input
                      className="form-control"
                      type={"file"}
                      accept="image/*"
                      ref={imageRef}
                      onChange={onThumbnailChange}
                    />
                  )}
                  {thumbnailError ? <p className="input-error-text">{thumbnailErrorMsg}</p> : null}

                  {/* Thumbnail Image Preview */}
                  {(previewUrl || currentThumbnail) && (
                    <Box sx={{ position: "relative", mt: 1 }}>
                      <img
                        src={previewUrl || getThumbnailSrc(currentThumbnail, "video_thumbnail")}
                        alt="Thumbnail Preview"
                        style={{
                          maxWidth: "150px",
                          maxHeight: "150px",
                          borderRadius: "8px",
                          border: "1px solid #CCCCCC",
                          objectFit: "cover",
                          display: "block"
                        }}
                      />
                      <IconButton
                        onClick={handleRemoveThumbnail}
                        size="small"
                        sx={{
                          position: "absolute",
                          top: -8,
                          right: -8,
                          color: "#FFFFFF",
                          bgcolor: "rgba(0, 0, 0, 0.6)",
                          "&:hover": {
                            bgcolor: "rgba(217, 45, 32, 0.9)",
                          },
                        }}
                      >
                        <FontAwesomeIcon icon={faXmark} size="xs" />
                      </IconButton>
                    </Box>
                  )}
                </Box>
              </div>
            </Box>
          </Box>

          {/* Progress Bar */}
          {!isEditMode && videoUploadBtnStatus && (
            <Box p={1} mt={2}>
              <LinearProgress variant="determinate" value={uploadProgress} />
              <p>Uploading... {uploadProgress}%</p>
              <p>Upload Speed: {uploadSpeed.toFixed(2)} Mbps</p>
            </Box>
          )}

          {!isEditMode && (
            <Box p={1}>
              <b><p className="text-center" style={{ color: 'red' }}>{videoUploadStatus}</p></b>
            </Box>
          )}

          <Box mt={isEditMode ? 3 : 5} sx={{ display: "flex", gap: 5, width: 1, justifyContent: 'center' }}>
            <TextButtonComponent
              text={"Cancel"}
              classStyle="btn btn-secondary"
              onButtonClick={() => onClose()}
              isDisabled={videoUploadBtnStatus}
            />
            <TextButtonComponent
              text={isEditMode ? "Update" : "Upload"}
              onButtonClick={() => handleFormSubmit()}
              isDisabled={videoUploadBtnStatus}
            />
          </Box>
        </DialogContent>
      </form>
    </>
  );
};

export default VideoForm;
